import { useState, useCallback, useEffect, useRef } from "react";
import markReviewHelpful from "@App/../services/reviewHelpfulService";

const STORAGE_KEY = "helpful_reviews";
const HELPFUL_SYNC_EVENT = "helpful_review_sync";

/**
 * Returns the set of review IDs the user has already marked as helpful
 * from localStorage. Returns empty set if unavailable.
 */
function getClickedReviews(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch {
    // Ignore parse errors
  }
  return new Set();
}

function saveClickedReview(reviewId: string): void {
  if (typeof window === "undefined") return;
  try {
    const existing = getClickedReviews();
    existing.add(reviewId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...existing]));
  } catch {
    // Ignore storage errors
  }
}

function removeClickedReview(reviewId: string): void {
  if (typeof window === "undefined") return;
  try {
    const existing = getClickedReviews();
    existing.delete(reviewId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...existing]));
  } catch {
    // Ignore storage errors
  }
}

/** Dispatch a custom event to notify other hook instances of a helpful state change */
function dispatchHelpfulSync(reviewId: string, isClicked: boolean, displayCount: number): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(HELPFUL_SYNC_EVENT, {
      detail: { reviewId, isClicked, displayCount },
    }),
  );
}

interface UseHelpfulClickReturn {
  isClicked: boolean;
  displayCount: number;
  handleClick: () => void;
  isLoading: boolean;
}

/**
 * Hook to manage helpful click state for a single review.
 * Supports toggle behavior — click to mark helpful, click again to unmark.
 * - Optimistic UI update on click
 * - Persists clicked state in localStorage
 * - Calls the server API (mark or unmark)
 * - Reverts on failure
 * - Syncs state across all mounted instances via custom event
 */
function useHelpfulClick(
  inventoryId: string | number,
  reviewId: string,
  initialCount: number,
): UseHelpfulClickReturn {
  const [isClicked, setIsClicked] = useState(false);
  const [displayCount, setDisplayCount] = useState(initialCount);
  const [isLoading, setIsLoading] = useState(false);
  const mountedRef = useRef(true);
  const isLocalUpdate = useRef(false);

  // Check localStorage on mount to restore "already clicked" state
  useEffect(() => {
    const clicked = getClickedReviews();
    if (clicked.has(reviewId)) {
      setIsClicked(true);
      setDisplayCount(initialCount + 1);
    }
    return () => {
      mountedRef.current = false;
    };
  }, [reviewId]);

  // Listen for sync events from other instances of the same review
  useEffect(() => {
    const handleSync = (e: Event) => {
      const { detail } = e as CustomEvent;
      if (detail.reviewId === reviewId && !isLocalUpdate.current) {
        setIsClicked(detail.isClicked);
        setDisplayCount(detail.displayCount);
      }
    };
    window.addEventListener(HELPFUL_SYNC_EVENT, handleSync);
    return () => window.removeEventListener(HELPFUL_SYNC_EVENT, handleSync);
  }, [reviewId]);

  const handleClick = useCallback(async () => {
    if (isLoading) return;

    isLocalUpdate.current = true;

    if (isClicked) {
      // Toggle OFF — unmark as helpful (local only)
      const newCount = Math.max(displayCount - 1, 0);
      setIsClicked(false);
      setDisplayCount(newCount);
      removeClickedReview(reviewId);
      dispatchHelpfulSync(reviewId, false, newCount);
    } else {
      // Toggle ON — mark as helpful
      const newCount = displayCount + 1;
      setIsClicked(true);
      setDisplayCount(newCount);
      setIsLoading(true);
      saveClickedReview(reviewId);
      dispatchHelpfulSync(reviewId, true, newCount);

      try {
        const response = await markReviewHelpful(inventoryId, reviewId);
        if (!mountedRef.current) return;

        if (response.success) {
          if (response.helpfulCount !== undefined && response.helpfulCount !== null) {
            setDisplayCount(response.helpfulCount);
            dispatchHelpfulSync(reviewId, true, response.helpfulCount);
          }
        } else {
          // Revert optimistic update
          removeClickedReview(reviewId);
          setIsClicked(false);
          setDisplayCount(initialCount);
          dispatchHelpfulSync(reviewId, false, initialCount);
        }
      } catch {
        // API not available — keep localStorage persistence (graceful degradation)
        if (!mountedRef.current) return;
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    }

    // Reset local update flag after the sync event has been dispatched
    setTimeout(() => {
      isLocalUpdate.current = false;
    }, 0);
  }, [isClicked, isLoading, inventoryId, reviewId, initialCount, displayCount]);

  return { isClicked, displayCount, handleClick, isLoading };
}

export default useHelpfulClick;
